/**
 * O*NET → Civilian-friendly competency label map.
 * Applied at render-time only — never mutates stored data.
 */
const COMPETENCY_MAP = {
  // Abilities
  'oral comprehension': 'Verbal Communication',
  'oral expression': 'Oral Presentation & Briefing',
  'written comprehension': 'Written Analysis',
  'written expression': 'Professional Writing',
  'problem sensitivity': 'Problem Identification & Resolution',
  'deductive reasoning': 'Analytical Reasoning',
  'inductive reasoning': 'Pattern Recognition & Analysis',
  'near vision': 'Detail-Oriented Inspection',
  'speech clarity': 'Clear Communication',
  'speech recognition': 'Active Listening',
  'fluency of ideas': 'Creative Problem-Solving',
  'originality': 'Innovative Thinking',
  'information ordering': 'Information Management & Organization',
  'category flexibility': 'Adaptive Classification',
  'selective attention': 'Focused Task Execution',
  'time sharing': 'Multitasking & Prioritization',
  'perceptual speed': 'Rapid Information Processing',
  'spatial orientation': 'Spatial Awareness & Navigation',
  'visualization': 'Strategic Visualization & Planning',
  'arm-hand steadiness': 'Precision Equipment Operation',
  'manual dexterity': 'Technical Equipment Handling',
  'finger dexterity': 'Fine Motor Precision',
  'control precision': 'Precision Control Systems',
  'multilimb coordination': 'Coordinated Equipment Operation',
  'response orientation': 'Rapid Response & Decision-Making',
  'reaction time': 'Emergency Response',
  'speed of closure': 'Rapid Situation Assessment',
  'flexibility of closure': 'Complex Pattern Recognition',
  'mathematical reasoning': 'Quantitative Analysis',
  'number facility': 'Numerical Analysis',
  'memorization': 'Knowledge Retention',
  'stamina': 'Physical Endurance & Resilience',
  'static strength': 'Physical Strength',
  'dynamic strength': 'Physical Fitness',
  'trunk strength': 'Core Physical Conditioning',
  'extent flexibility': 'Physical Flexibility',
  'gross body coordination': 'Physical Coordination',
  'gross body equilibrium': 'Balance & Stability',
  'far vision': 'Long-Range Observation',
  'night vision': 'Low-Light Operations',
  'depth perception': 'Depth & Distance Assessment',
  'glare sensitivity': 'Visual Acuity',
  'hearing sensitivity': 'Acoustic Monitoring',
  'auditory attention': 'Audio Signal Detection',
  'sound localization': 'Directional Sound Analysis',

  // Skills
  'active listening': 'Active Listening',
  'critical thinking': 'Critical Thinking',
  'reading comprehension': 'Reading Comprehension',
  'speaking': 'Verbal Communication',
  'writing': 'Professional Writing',
  'active learning': 'Continuous Learning & Development',
  'learning strategies': 'Training & Development',
  'monitoring': 'Performance Monitoring',
  'social perceptiveness': 'Interpersonal Awareness',
  'coordination': 'Cross-Functional Coordination',
  'persuasion': 'Persuasion & Negotiation',
  'negotiation': 'Negotiation & Conflict Resolution',
  'instructing': 'Training & Instruction',
  'service orientation': 'Client & Stakeholder Service',
  'complex problem solving': 'Complex Problem-Solving',
  'operations analysis': 'Operations Analysis',
  'technology design': 'Technology Design & Implementation',
  'equipment selection': 'Equipment Selection & Procurement',
  'installation': 'Systems Installation',
  'programming': 'Software & Systems Programming',
  'operation monitoring': 'Systems Monitoring & Diagnostics',
  'operation and control': 'Equipment Operation & Control',
  'equipment maintenance': 'Preventive & Corrective Maintenance',
  'troubleshooting': 'Technical Troubleshooting',
  'repairing': 'Equipment Repair & Restoration',
  'quality control analysis': 'Quality Assurance & Control',
  'judgment and decision making': 'Judgment & Decision-Making',
  'systems analysis': 'Systems Analysis',
  'systems evaluation': 'Systems Evaluation & Optimization',
  'time management': 'Time Management',
  'management of financial resources': 'Budget & Financial Management',
  'management of material resources': 'Supply Chain & Logistics Management',
  'management of personnel resources': 'Team Leadership & Personnel Management',
  'science': 'Scientific Analysis',
  'mathematics': 'Mathematical Modeling',

  // Knowledge
  'administration and management': 'Administration & Management',
  'clerical': 'Administrative Procedures',
  'economics and accounting': 'Economics & Financial Analysis',
  'sales and marketing': 'Marketing & Outreach',
  'customer and personal service': 'Customer Service & Relations',
  'personnel and human resources': 'Human Resources Management',
  'production and processing': 'Production & Process Management',
  'food production': 'Food Service & Safety Management',
  'computers and electronics': 'Information Technology',
  'engineering and technology': 'Engineering & Technology',
  'design': 'Design & Technical Drafting',
  'building and construction': 'Construction & Infrastructure',
  'mechanical': 'Mechanical Systems',
  'mathematics': 'Mathematics & Quantitative Methods',
  'physics': 'Applied Physics',
  'chemistry': 'Chemical Analysis',
  'biology': 'Biological Sciences',
  'psychology': 'Behavioral Science',
  'sociology and anthropology': 'Social & Cultural Analysis',
  'geography': 'Geography & Geospatial Analysis',
  'medicine and dentistry': 'Medical & Health Sciences',
  'therapy and counseling': 'Counseling & Support Services',
  'education and training': 'Education & Training',
  'english language': 'English Language & Communications',
  'foreign language': 'Foreign Language Proficiency',
  'fine arts': 'Visual & Creative Arts',
  'history and archeology': 'Historical Analysis',
  'philosophy and theology': 'Ethics & Philosophy',
  'public safety and security': 'Public Safety & Security',
  'law and government': 'Law, Policy & Government',
  'telecommunications': 'Telecommunications & Networking',
  'communications and media': 'Communications & Media',
  'transportation': 'Transportation & Logistics',
};

/**
 * Convert an O*NET competency label into a civilian-friendly version.
 * Returns the mapped label if found, otherwise returns the original with title casing.
 */
export function civilianizeCompetency(raw) {
  if (!raw) return '';
  const key = raw.toLowerCase().trim();
  if (COMPETENCY_MAP[key]) return COMPETENCY_MAP[key];
  // Title-case fallback: capitalize first letter of each word
  return raw.replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Civilianize and deduplicate an array of competency names.
 */
export function civilianizeCompetencies(rawList) {
  if (!rawList?.length) return [];
  const seen = new Set();
  const result = [];
  for (const item of rawList) {
    const label = civilianizeCompetency(item);
    const key = label.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(label);
    }
  }
  return result;
}

export default COMPETENCY_MAP;
